/**
 * Cookie Manager.
 * Ask the user which cookies are allowed and handle/delegate the result.
 *
 * @author Sven Eberth
 * @version 1.3.0
 */
export default class CookieManager {

	constructor() {
		this.html = document.querySelector('html');
		this.cookieDialog = document.querySelector('.js-cookie');
		this.cookiePopup = document.querySelector('.js-cookie-popup');
		this.cookieConfig = document.querySelector('.js-cookie-conf-popup');

		this._initializePopup();
		this._checkForCookies();

		document.dispatchEvent(new CustomEvent('cookie-manager-ready',
			{detail: {allowedCookies: this.allowedCookies}}));
	}

	/**
	 * Open the cookie dialog.
	 */
	show() {
		document.querySelectorAll('.js-cookie-switch')
			.forEach(input => input.required ? null : input.checked = false);

		for (name of this.allowedCookies) {
			let input = document.querySelector(`#cookie-conf-${name}`);
			if (input !== null) input.checked = true;
		}

		this.cookieDialog.classList.add('is-active');
		this.cookiePopup.classList.remove('is-hidden');
		this.cookieConfig.classList.add('is-hidden');

		this.html.classList.add('no-scroll');
	}

	/**
	 * Close the cookie dialog. Will not handle the changes.
	 */
	close() {
		this.cookieDialog.classList.remove('is-active');
		this.cookiePopup.classList.add('is-hidden');
		this.cookieConfig.classList.add('is-hidden');
		this.html.classList.remove('no-scroll');
	}

	/**
	 * Initialize the cookie-popup.
	 * Bind the action for the buttons.
	 * @private
	 */
	_initializePopup() {
		document.querySelector('.js-cookie-accept-all').addEventListener('click', e => {
			this._updateConsent(true);
			this._handleConsent(false);
			this.close();
		});

		document.querySelector('.js-cookie-accept-selection').addEventListener('click', e => {
			this._updateConsent();
			this._handleConsent()
			this.close();
		});

		document.querySelector('.js-cookie-conf').addEventListener('click', e => {
			this.cookiePopup.classList.add('is-hidden');
			this.cookieConfig.classList.remove('is-hidden');
		});

		document.querySelector('.js-cookie-decline').addEventListener('click', e => {
			/* Bitte einfügen */
			console.log("Cookies declined!")
			this._rejectConsent();
			this._handleConsent(false);
			this.close();
		});
	}

	/**
	 * Check whether the user has already agreed to the cookies or not.
	 * Show the dialog or handle the consent.
	 * @private
	 */
	_checkForCookies() {
		if (this.allowedCookies.length || document.getElementsByClassName('preventcookiehint').length) {
			// Apply the consent that's stored in LocalStorage (could be nothing)
			this._handleConsent();

		} else if (/bot|google|bing|baidu|yandex/i.test(navigator.userAgent)) {
			// Accept all cookies for crawler and bots
			this.cookieConfig.querySelectorAll('.js-cookie-switch')
				.forEach(input => input.checked = true);
			this._updateConsent();
			this._handleConsent();

		} else if (parseInt(navigator.doNotTrack) === 1) {
			// DNT: Accept only necessary cookies
			// (they are already checked by default, so they must only be collected and handled)
			this._updateConsent();
			this._handleConsent();
			const popup = document.getElementById('cookiemanager-dnt-popup');
			popup.classList.add('is-active');
			setTimeout(() => popup.classList.add('popup--fade'), 1000);

		} else {
			// No consent; show the dialog
			this.show();
		}
	}

	/**
	 * Collect the agreed cookies from the inputs and save the consent.
	 * @param all Agree all or only selected cookies.
	 * @private
	 */
	_updateConsent(all) {
		let inputs = this.cookieConfig.querySelectorAll('.js-cookie-switch');
		let allowedCookies = [];

		inputs.forEach((item) => {
			let itemName = item.name.split('-');
			let category = itemName[2];
			let name = itemName.slice(3).join('-');

			if (all || item.checked) {
				allowedCookies.push(`${category}-${name}`);
			}
		})

		let oldValue = this.allowedCookies;
		this.allowedCookies = allowedCookies;
		document.dispatchEvent(new CustomEvent('cookie-manager-consent-change',
			{detail: {allowedCookies: allowedCookies, old: oldValue}}));
	}

	/**
	 * Reject all cookies except the necessary ones and save the consent.
	 * @private
	 */
	_rejectConsent() {
		let inputs = this.cookieConfig.querySelectorAll('.js-cookie-switch');
		let allowedCookies = [];


		inputs.forEach((item) => {
			let itemName = item.name.split('-');
			let category = itemName[2];
			let name = itemName.slice(3).join('-');
			
			if (category === "necessary") {
				allowedCookies.push(`${category}-${name}`);
			}
		})

		let oldValue = this.allowedCookies;
		this.allowedCookies = allowedCookies;
		document.dispatchEvent(new CustomEvent('cookie-manager-consent-change',
			{detail: {allowedCookies: allowedCookies, old: oldValue}}));
	}

	/**
	 * Handle the consent.
	 * Fire events for each allowed cookies and load the scripts.
	 * @private
	 */
	_handleConsent() {
		this.allowedCookies.forEach(name => {
			console.log("fire event", name)
			document.dispatchEvent(new Event(`cookie-allowed-${name}`));
		})

		document.querySelectorAll('.js-cookiemanager:not(.is-loaded)').forEach((script) => {
			if (~this.allowedCookies.indexOf(script.dataset.requires)) {
				script.src = script.getAttribute('data-src') || script.getAttribute('data-imgsrc');
				script.classList.add('is-loaded');
			}
		})
	}

	/**
	 * Helper method to create a cookie.
	 * Currently not used in this manager.
	 * @param key Name of the cookie.
	 * @param value Value of the cookie.
	 * @param lifeTime Lifetime of the cookie (microseconds).
	 */
	static setCookie(key, value, lifeTime) {
		let expires = new Date();
		expires.setTime(expires.getTime() + (lifeTime || (365 * 24 * 60 * 60 * 1000))); // 365 days
		document.cookie = key + '=' + value + ';expires=' + expires.toUTCString() + ';path=/';
	}

	/**
	 * Helper method to get a cookie value.
	 * Currently not used in this manager.
	 * @param key Name of the cookie.
	 * @returns {string|null} The cookie value or null if the cookie doesn't exist.
	 */
	static getCookie(key) {
		let keyValue = document.cookie.match('(^|;) ?' + key + '=([^;]*)(;|$)');
		return keyValue ? keyValue[2] : null;
	}

	/**
	 * Helper method to load a script.
	 * @param src The script url.
	 * @returns {Promise<Element>}
	 */
	static loadScript(src) {
		return new Promise(function (resolve, reject) {
			let script = document.createElement('script');
			script.type = 'text/javascript';
			script.charset = 'utf-8';
			script.async = true;
			script.src = src;
			let firstScriptElement = document.getElementsByTagName('script')[0];
			firstScriptElement.parentNode.insertBefore(script, firstScriptElement);

			resolve(script);
		});
	}

	/**
	 * Allow one ore multiple cookie(s) and handle the new consent.
	 * @param names The name(s) of the cookies.
	 */
	allowCookie(...names) {
		let currentConsent = new Set(this.allowedCookies);
		names.forEach(name => currentConsent.add(name))
		this.allowedCookies = Array.from(currentConsent);
		this._handleConsent();
	}

	/**
	 * Store the value of the allowed cookies in the local storage.
	 * @param value The new value.
	 */
	set allowedCookies(value) {
		if (!Array.isArray(value))
			throw new TypeError('Value must be an array');

		localStorage.setItem('allowed-cookies', JSON.stringify(value));
	}

	/**
	 * Get the value of the allowed cookies from the local storage.
	 * @returns {*[]} Array of the allowed cookies.
	 */
	get allowedCookies() {
		return JSON.parse(localStorage.getItem('allowed-cookies')) || [];
	}
}
